import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Avatar, ToggleButton, ToggleButtonGroup, CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import useApiManager from '../../utils/Api/apiManager';
import { ProposalStatus } from '../../utils/enums';
import { toast } from 'sonner';
import Drawer from '../../components/Proposals/Details/Drawer';
import BasicDetails from '../../components/Proposals/Details/BasicDetails/BasicDetails';
import FullDetails from '../../components/Proposals/Details/FullDetails/FullDetails';
import DescriptionIcon from '@mui/icons-material/Description';
import Notes from '../../components/Common/Notes/Notes';
import Attachments from '../../components/Common/Attachments/Attachments'

const ProposalDetails = () => {
    const { proposalId } = useParams(); // Get proposalId from URL
    const [proposalDetails, setProposalDetails] = useState(null);
    const [notes, setNotes] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const detailsRef = useRef(null);
    const notesRef = useRef(null);
    const attachmentsRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [completing, setCompleting] = useState(false);
    const { get, put } = useApiManager();

    const sentProposalStatusId = ProposalStatus.find((s) => s.label === 'Sent')?.value;

    const scrollToSection = (ref) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchProposalDetails = async () => {
        setLoading(true);

        try {
            const data = await get(`/proposals/${proposalId}`);
            setProposalDetails(data);
            const topic = data.topic ?? '';
            document.title = `${topic} (Proposal) - iLead CRM`.trim() || 'Proposal Details';
        } catch (error) {
            console.error('Error fetching proposal details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchNotes = async () => {
        setLoading(true);

        try {
            const notes = await get(`/proposals/${proposalId}/notes`);
            setNotes(notes);
        } catch (error) {
            console.error('Error fetching proposal notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAttachments = async () => {
        setLoading(true);

        try {
            const attachments = await get(`/proposals/${proposalId}/attachments`);
            setAttachments(attachments);
        } catch (error) {
            console.error('Error fetching proposal attachments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAssigneesChange = (newAssignees) => {
        // Update the local state with new assignees
        setProposalDetails(prev => ({
            ...prev,
            assignTo: newAssignees
        }));
    };

    const handleComplete = async () => {
        if (proposalId == null || sentProposalStatusId == null) return;
        setCompleting(true);
        try {
            await put(`/proposals/${proposalId}/status`, sentProposalStatusId);
            toast.success('Proposal marked as sent.');
            await fetchProposalDetails();
        } catch (error) {
            console.error('Error updating proposal status:', error);
            toast.error('Could not update proposal status.');
        } finally {
            setCompleting(false);
        }
    };

    useEffect(() => {
        fetchProposalDetails();
        fetchNotes();
        fetchAttachments();
    }, []);

    return (
        <div style={{}}>
            <Paper elevation={0} sx={{
                p: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DescriptionIcon />
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <Typography sx={{ fontWeight: 'bold', fontSize: '1.35rem' }}>
                            {proposalDetails?.topic}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleComplete}
                            disabled={
                                completing ||
                                proposalDetails?.proposalStatus === sentProposalStatusId
                            }
                        >
                            {completing ? <CircularProgress size={22} color="inherit" /> : 'Complete'}
                        </Button>
                        <Link to={`/proposals/edit/${proposalId}`}>
                            <Button variant="outlined">Edit</Button>
                        </Link>
                    </Box>
                </Box>
            </Paper >
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box id='drawer' sx={{ display: 'flex' }}>
                    <Drawer
                        scrollToSection={scrollToSection}
                        sections={{
                            detailsRef,
                            notesRef,
                            attachmentsRef
                        }} />
                </Box>
                <Box id='main' sx={{ flexGrow: 1, paddingTop: 3, paddingRight: 5 }}>
                    <Box sx={{ '& > * > *': { marginBottom: 3 } }}>

                        <div id='overview'>
                            <div>
                                {/* <BasicDetails data={leadDetails} /> */}
                                <BasicDetails 
                                    data={proposalDetails} 
                                    onAssigneesChange={handleAssigneesChange}
                                />
                            </div>
                           
                            <div ref={detailsRef}>
                                <FullDetails data={proposalDetails} />
                            </div>
                            <div ref={notesRef}>
                                <Notes data={notes} fetchNotes={fetchNotes} entity={'proposals'} />
                            </div>
                            <div ref={attachmentsRef}>
                                <Attachments data={attachments} fetchAttachments={fetchAttachments} entity={'proposals'}/>
                            </div>
                            
                        </div>
                        <div id='history'></div>
                    </Box>
                </Box>
            </Box>

        </div>
    )
};

export default ProposalDetails;

